/*
 * ===============================================================================================================
 *                                Copyright 2024, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { LuiSelect, LuiModal } from '@jda/lui-common-component-library-mui5';
import { AlertCircle } from '@jda/lui-common-icon-library-mui5';
import { Button, Checkbox, TextField, Typography, Box, FormControlLabel } from '@mui/material';
import Loader from '../../shared/Loader';
import { ErrorMessage } from '../../shared/ErrorMessage/ErrorMessage';
import {
  selectLocationTypes,
  selectLocationTypesLoading,
  selectLocationTypesError
} from '../../store/locations/locationTypesSelector';
import { fetchLocationTypes } from '../../store/locations/locationTypes';
import { createLocation, getLocationById, updateLocation } from '../../services/locations';
import { getIdpById, createIdp, updateIdp } from '../../services/setting';
import { displaySnackbar } from '../../store/snackBar/snackBarActionCreator';
import interpolateString from '../../utility/stringInterpolation';
import { useStyles } from './styles';
import {
  LOCATION_TYPE,
  LOCATION_NAME,
  LOCATION_ADDRESS,
  ADD,
  SAVE,
  CANCEL,
  ADD_LOCATION,
  EDIT_LOCATION,
  IDP_DISABLE_WARN_MSG,
  DROPDOWN_MAX_HEIGHT,
  HELPER_MESSAGE,
  HELPER_ERROR_MESSAGE,
  LOCATION_NAME_VALIDATION_MESSAGE,
  LOCATION_CREATE_SUCCESS,
  LOCATION_CREATE_ERROR,
  LOCATION_EDIT_SUCCESS,
  LOCATION_EDIT_ERROR,
  DISPLAY_NAME
} from './constants';
import { ILocationType } from '../../models/Locations/Locations';

interface ILocationData {
  locationType: string | null;
  displayName: string;
  locationName: string;
  locationAddress: string;
}

interface AddLocationDialogProps {
  onSave: () => void;
  onClose: () => void;
  locationId: any;
}

const AddLocationDialog = ({ onSave, onClose, locationId }: AddLocationDialogProps) => {
  const [locationIdp, setLocationIdp] = useState<any>();
  const [locationData, setLocationData] = useState<ILocationData>({
    locationType: null,
    displayName: '',
    locationName: '',
    locationAddress: ''
  });
  const [isLoading, setIsLoading] = useState(Boolean(locationId));
  const locationTypes: ILocationType[] = useSelector(selectLocationTypes);
  const locationsTypesLoading = useSelector(selectLocationTypesLoading);
  const locationTypesError = useSelector(selectLocationTypesError);

  const dispatch = useDispatch();
  const { classes } = useStyles();

  useEffect(() => {
    if (locationTypes.length === 0) {
      fetchLocationTypes(dispatch);
    }
  }, [dispatch, locationTypes.length]);

  useEffect(() => {
    const fetchLocationData = async () => {
      if (locationId === null) {
        return;
      }

      setIsLoading(true);

      try {
        const locationById = await getLocationById(locationId);

        setLocationData({
          locationType: locationById.locationType,
          locationName: locationById.name,
          displayName: locationById.displayName,
          locationAddress: locationById.locationAddress
        });

        const locationIdp = locationById.idpId ? await getIdpById(locationById.idpId) : undefined;
        setLocationIdp(locationIdp);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLocationData();
  }, [locationId]);

  const locationTypeData = useMemo(
    () =>
      locationTypes.map(locationType => ({ ...locationType, key: locationType.name, displayName: locationType.name })),
    [locationTypes]
  );

  const isLocationIdpEnabled = (locationIdp?.enabled ?? false) as boolean;

  const defaultFormValues = useMemo(() => ({
    ...locationData,
    isLocationIdpEnabled
  }), [locationData, isLocationIdpEnabled]);

  const {
    control,
    handleSubmit,
    formState: { isSubmitting }
  } = useForm({
    values: defaultFormValues
  });

  const handleSaveLocation = handleSubmit(
    async ({ locationType, displayName, locationName, locationAddress, isLocationIdpEnabled: isCheckBoxChecked }) => {
      try {
        const locationPayload = {
          locationType: locationType as string,
          displayName,
          ...(locationId === null && { name: locationName }),
          locationAddress
        };

        if (locationId) {
          await updateLocation(locationId, locationPayload);
          if (isCheckBoxChecked !== isLocationIdpEnabled) {
            if (locationIdp) {
              await updateIdp(locationIdp.id, { enabled: isCheckBoxChecked });
            } else {
              await createIdp({ identityProviderType: 'Native', locationId });
            }
          }
        } else {
          const location = await createLocation(locationPayload);

          if (isCheckBoxChecked && location?.id) {
            await createIdp({ identityProviderType: 'Native', locationId: location.id });
          }
        }

        onSave();
        onClose();
        dispatch(
          displaySnackbar(
            interpolateString([displayName], locationId ? LOCATION_EDIT_SUCCESS : LOCATION_CREATE_SUCCESS),
            'success'
          )
        );
      } catch (error: any) {
        dispatch(
          displaySnackbar(
            `${locationId ? LOCATION_EDIT_ERROR : LOCATION_CREATE_ERROR}: ${error.response.data.message || error.message}`,
            'error'
          )
        );
      }
    }
  );

  return (
    <LuiModal
      modalTitle={<Typography variant="h4">{locationId === null ? ADD_LOCATION : EDIT_LOCATION}</Typography>}
      isOpen={true}
      handleClose={onClose}
      topDivider={true}
      bottomDivider={false}
      modalSize="xs"
      data-testid="location-modal"
      secondaryAction={
        <Button
          onClick={onClose}
          color="primary"
          variant="outlined"
          style={{ background: 'white' }}
          disableElevation={true}
        >
          {CANCEL}
        </Button>
      }
      primaryAction={
        <Button
          onClick={handleSaveLocation}
          variant="contained"
          color="primary"
          disableElevation={true}
          disabled={isSubmitting}
        >
          {locationId === null ? ADD : SAVE}
        </Button>
      }
    >
      <Controller
        control={control}
        name="locationType"
        rules={{
          required: interpolateString(['type'], HELPER_ERROR_MESSAGE)
        }}
        render={({ field: { value, onChange }, fieldState: { invalid, error } }) => (
          <LuiSelect
            id={'dropdown-select-location-type'}
            itemData={locationTypeData}
            classes={{ select: classes.itemDropdownType, paper: classes.paperDropdown }}
            inputLabel={LOCATION_TYPE}
            labelDisplay={false}
            maxHeight={DROPDOWN_MAX_HEIGHT}
            size={'extra-small'}
            selectedItem={locationTypeData.find(option => option.name === value)}
            required={true}
            error={invalid}
            helperText={error?.message ?? HELPER_MESSAGE}
            onSelectionChange={event => {
              onChange(event.target.value.key);
            }}
            data-testid="dropdown-select-location-type"
          />
        )}
      />

      <Controller
        control={control}
        name="displayName"
        rules={{
          required: interpolateString(['display name'], HELPER_ERROR_MESSAGE)
        }}
        render={({ field, fieldState: { invalid, error } }) => (
          <TextField
            {...field}
            id={'displayName'}
            variant="standard"
            fullWidth={true}
            label={DISPLAY_NAME}
            helperText={error?.message ?? HELPER_MESSAGE}
            required={true}
            error={invalid}
            classes={{ root: classes.rootInputName }}
            data-testid="location-name-input"
          />
        )}
      />

      <Controller
        control={control}
        name="locationName"
        rules={{
          required: interpolateString(['name'], HELPER_ERROR_MESSAGE),
          pattern: { value: /^[a-zA-Z0-9_-]{5,20}$/, message: LOCATION_NAME_VALIDATION_MESSAGE }
        }}
        disabled={Boolean(locationId)}
        render={({ field, fieldState: { invalid, error } }) => (
          <TextField
            {...field}
            variant="standard"
            fullWidth={true}
            label={LOCATION_NAME}
            helperText={error?.message ?? HELPER_MESSAGE}
            error={invalid}
            classes={{ root: classes.rootInputName }}
            id={'locationName'}
          />
        )}
      />

      <Controller
        control={control}
        name="locationAddress"
        render={({ field }) => (
          <TextField
            {...field}
            id={'locationAddress'}
            variant="standard"
            fullWidth={true}
            label={LOCATION_ADDRESS}
            classes={{ root: classes.rootInputAddress }}
            data-testid="location-address-input"
          />
        )}
      />

      <Controller
        control={control}
        name="isLocationIdpEnabled"
        render={({ field: { value, onChange } }) => (
          <Box>
            <FormControlLabel
              onChange={onChange}
              control={<Checkbox color='primary' checked={value} />}
              label={<span>Enable <Typography variant="body1" component="span" style={{ fontWeight: 'bold' }}>BlueYonder User Store</Typography></span>}
            />
            <Typography variant="body2" classes={{ root: classes.checkBoxDescription }}>BlueYonder User Store lets you add seasonal or
              temporary users to this sub-realm (e.g. warehouse workers).</Typography>
            {isLocationIdpEnabled && !value && (
              <Box className={classes.alertMsg}>
                <Box className={classes.warningIcon}>
                  <AlertCircle />
                </Box>
                <Typography variant="body1">
                  <Box fontWeight="normal">
                    {interpolateString([locationId === null ? ADD : SAVE], IDP_DISABLE_WARN_MSG)}
                  </Box>
                </Typography>
              </Box>
            )}
          </Box>
        )}
      />

      {locationTypesError && <ErrorMessage message={locationTypesError} />}

      <Loader
        showLoader={locationsTypesLoading || isLoading || isSubmitting}
        isCenter={true}
        customClasses={classes.loader}
      />
    </LuiModal>
  );
};

export default AddLocationDialog;
